import { format } from 'date-fns'
import { fromZonedTime, toZonedTime } from 'date-fns-tz'

/**
 * Parses a time string and returns a Date object for display in time pickers.
 * Handles both legacy ISO format (before 2026) and new UTC format (from 2026).
 *
 * @param timeStr - ISO time string to parse
 * @param profileDate - Profile date to determine which format to use
 * @returns Date object for display or null if timeStr is undefined
 */
export const parseTimeAsLocal = (timeStr: string | undefined, profileDate: string | Date | undefined): Date | null => {
    if (!timeStr) return null
    const cutoffDate = new Date('2026-01-01')
    const isBeforeCutoff = profileDate && new Date(profileDate) < cutoffDate

    const date = new Date(timeStr)
    if (isBeforeCutoff) {
        // Before 2026: Display as-is (ISO format with local timezone)
        return date
    } else {
        // From 2026: Interpret UTC as local time
        return new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), date.getUTCHours(), date.getUTCMinutes(), 0, 0)
    }
}

/**
 * Formats a time string for display (HH:mm format).
 * Handles both legacy ISO format (before 2026) and new UTC format (from 2026).
 *
 * @param timeStr - ISO time string to format
 * @param profileDate - Profile date to determine which format to use
 * @returns Formatted time string (HH:mm) or empty string if timeStr is undefined
 */
export const formatTimeAsLocal = (timeStr: string | undefined, profileDate: string | Date | undefined): string => {
    if (!timeStr) return ''
    const cutoffDate = new Date('2026-01-01')
    const isBeforeCutoff = profileDate && new Date(profileDate) < cutoffDate

    if (isBeforeCutoff) {
        // Before 2026: Use toZonedTime for ISO format
        return format(toZonedTime(timeStr + 'Z', 'Europe/Berlin'), 'HH:mm')
    } else {
        // From 2026: Interpret UTC as local time
        const timeStrWithZ = timeStr.endsWith('Z') ? timeStr : timeStr + 'Z'
        const date = new Date(timeStrWithZ)
        return format(new Date(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate(), date.getUTCHours(), date.getUTCMinutes()), 'HH:mm')
    }
}

/**
 * Creates an ISO time string from a Date object.
 * Handles both legacy ISO format (before 2026) and new UTC format (from 2026).
 *
 * @param date - Date object with time to store
 * @param profileDate - Profile date to determine which format to use
 * @returns ISO time string
 */
export const createTimeString = (date: Date, profileDate: string | Date | undefined): string => {
    if (!profileDate) return date.toISOString()

    const baseDate = new Date(profileDate)
    const cutoffDate = new Date('2026-01-01')

    if (baseDate < cutoffDate) {
        // Before 2026: Store as ISO (local timezone)
        const newDate = new Date(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate(), date.getHours(), date.getMinutes(), 0, 0)
        return newDate.toISOString()
    } else {
        // From 2026: Store as UTC (timezone-independent)
        const newDate = new Date(Date.UTC(baseDate.getFullYear(), baseDate.getMonth(), baseDate.getDate(), date.getHours(), date.getMinutes(), 0, 0))
        return newDate.toISOString()
    }
}

/**
 * Parses a template time string and applies it to a target date.
 * Templates are always stored as UTC, so extract UTC hours/minutes.
 *
 * @param raw - Template time string
 * @param baseDate - Target date to apply template to
 * @param dateIso - Target date ISO string for format determination
 * @returns ISO string in appropriate format or undefined
 */
export const parseTemplateDateToIso = (raw: string | undefined, baseDate: Date, dateIso: string): string | undefined => {
    if (!raw) return undefined
    // some templates use placeholder zero dates
    if (raw.startsWith && raw.startsWith('0001')) return undefined

    const t = new Date(raw)
    if (isNaN(t.getTime())) return undefined

    // Templates are always stored as UTC, so extract UTC hours/minutes
    const timeDate = new Date(baseDate)
    timeDate.setHours(t.getUTCHours(), t.getUTCMinutes(), 0, 0)
    return createTimeString(timeDate, dateIso)
}

/**
 * Checks if a date is in January.
 * Uses string comparison to avoid timezone conversion issues.
 *
 * @param dateStr - Date string to check (format: "YYYY-MM-DDTHH:mm:ss" with or without Z)
 * @returns true if date is in January, false otherwise
 */
export const isDateJan = (dateStr: string | undefined): boolean => {
    if (!dateStr) return false
    
    const datePortion = dateStr.substring(0, 10)
    const month = datePortion.substring(5, 7)
    
    return month === '01'
}

/**
 * Parses an ISO-like DateTime value to a Date.
 */
export const parseIsoDateTimeWithTimeZoneFallback = (value: unknown, timeZone: string): Date | null => {
    if (!value) return null
    if (value instanceof Date) return isNaN(value.getTime()) ? null : value

    if (typeof value !== 'string') {
        const d = new Date(value as any)
        return isNaN(d.getTime()) ? null : d
    }

    const trimmed = value.trim()
    if (!trimmed) return null

    const hasTimeZoneDesignator = /([zZ]|[+-]\d{2}:\d{2})$/.test(trimmed)
    const d = hasTimeZoneDesignator ? new Date(trimmed) : fromZonedTime(trimmed, timeZone)
    return isNaN(d.getTime()) ? null : d
}

export const parseIsoDateTimeAsBerlin = (value: unknown): Date | null => parseIsoDateTimeWithTimeZoneFallback(value, 'Europe/Berlin')
